import { Fragment, createElement, type ReactNode } from 'react';
import { getLinkItemAt } from '../links/link-item';
import { resolveLinkAttributes } from '../links/resolve-link-attributes';
import type { AnchorAttributes } from '../links/resolve-link-attributes';
import { getSpanStyleItemAt } from '../span-styles/span-style-item';
import { resolveSpanStyleAttributes } from '../span-styles/resolve-span-style-attributes';
import type { SpanStyleAttributes } from '../span-styles/resolve-span-style-attributes';
import { parseInlineMarkers } from './parse-inline-markers';
import type { InlineMarks, MarkupRun, MarkupSpanRun } from './parse-inline-markers';

/**
 * Which element each mark renders as, outermost first.
 *
 * `s` rather than `del`: struck CMS copy is text that no longer applies (an old
 * price), not an edit to the document.
 */
const MARK_TAGS: ReadonlyArray<readonly [keyof InlineMarks, 'strong' | 'em' | 's']> = [
  ['bold', 'strong'],
  ['italic', 'em'],
  ['strike', 's'],
];

const hasMark = (marks: InlineMarks): boolean => marks.bold || marks.italic || marks.strike;

/**
 * The anchor stays the innermost element, so marks wrap it rather than the
 * other way round — `<strong><a>…</a></strong>`. Keeps the one rule from
 * `INNER_LINK_TAGS_ARCHITECTURE.md` true no matter what else a span carries.
 *
 * A styling entry becomes the outermost element, outside the marks: the marks
 * say what these words *are*, and the styling says how this run of words looks,
 * which is the container of the other two. It also means an author who sets a
 * colour on bold words does not have to know which element the colour lands on.
 */
const renderSpan = (
  run: MarkupSpanRun,
  anchor: AnchorAttributes | undefined,
  spanStyle: SpanStyleAttributes | undefined
): ReactNode => {
  let node: ReactNode = anchor === undefined ? run.text : createElement('a', anchor, run.text);

  for (let position = MARK_TAGS.length - 1; position >= 0; position -= 1) {
    const [mark, tag] = MARK_TAGS[position];
    if (run.marks[mark]) {
      node = createElement(tag, null, node);
    }
  }

  return spanStyle === undefined ? node : createElement('span', spanStyle, node);
};

/** What a run contributes when nothing about it needs an element. */
const plainTextOf = (run: MarkupRun): string => (run.kind === 'break' ? '' : run.text);

/**
 * Renders authored text with its `{…|…}` markers resolved — marks as elements,
 * indexes against a link list, `.n` against a styling list, `{br|}` as a line
 * break.
 *
 * Every element is phrasing content and sits inside whatever tag the caller
 * wraps this in; nothing here ever becomes the caller's root element, so the
 * wrapper decisions in `MINIMAL_MARKUP_ARCHITECTURE.md` are unaffected.
 *
 * When no run needs an element the return value is a plain string: the same
 * React output as text with no markup support at all. A span whose link or
 * styling entry is missing, cleared, or rejected contributes its text, so a
 * broken reference degrades to prose rather than leaving a `{1|…}` shell,
 * emitting an `<a>` without an `href`, or wrapping words in a `<span>` that
 * carries nothing.
 */
export function renderInlineMarkup(
  text: string,
  links: unknown,
  spanStyles?: unknown
): ReactNode {
  const runs = parseInlineMarkers(text);

  const anchors = runs.map((run) =>
    run.kind === 'span' && run.index !== undefined
      ? resolveLinkAttributes(getLinkItemAt(links, run.index))
      : undefined
  );

  const spanStyleAttributes = runs.map((run) =>
    run.kind === 'span' && run.styleIndex !== undefined
      ? resolveSpanStyleAttributes(getSpanStyleItemAt(spanStyles, run.styleIndex))
      : undefined
  );

  const needsElement = runs.some(
    (run, position) =>
      run.kind === 'break' ||
      (run.kind === 'span' &&
        (anchors[position] !== undefined ||
          spanStyleAttributes[position] !== undefined ||
          hasMark(run.marks)))
  );

  if (!needsElement) {
    return runs.map(plainTextOf).join('');
  }

  return runs.map((run, position) => {
    if (run.kind === 'text') {
      return run.text;
    }
    if (run.kind === 'break') {
      return <br key={position} />;
    }
    return (
      <Fragment key={position}>
        {renderSpan(run, anchors[position], spanStyleAttributes[position])}
      </Fragment>
    );
  });
}
